import { useEffect } from 'react';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  Button, ButtonGroup, IconButton, Input, Slider,
} from '@mui/material';
import {
  Add, Pause, PlayArrow, Remove,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { MetronomeProps } from './Metronome';

const useStyles = makeStyles()((theme) => ({
  metronomeControls: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  controlGroup: {
    width: '100%',
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(1),
    gap: theme.spacing(1),
  },
  bpmSliderContainer: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    width: '20ch',
    display: 'flex',
    flexDirection: 'row',
  },
  bpmInput: {
    width: '5ch !important',
  },
}));

export interface MetronomeControlsProps extends MetronomeProps {
  onBpmChange: (bpm: number) => void;
  onTimeSigChange: (timeSig: number) => void;
  onToggle?: () => void;
}

const MAX_BPM = 244;
const MIN_BPM = 30;

const MetronomeControls: React.FC<MetronomeControlsProps> = ({
  bpm,
  timeSig,
  isOn,
  onBpmChange,
  onTimeSigChange,
  onToggle,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [inputBpm, setInputBpm] = React.useState(bpm);

  useEffect(() => {
    setInputBpm(bpm);
  }, [bpm]);

  const handleBpmChange = (newValue: number) => {
    if (Number.isNaN(newValue)) {
      setInputBpm(bpm);
      return;
    }
    if (newValue < MIN_BPM) {
      onBpmChange(MIN_BPM);
    } else if (newValue > MAX_BPM) {
      onBpmChange(MAX_BPM);
    } else {
      onBpmChange(newValue);
    }
  };

  return (
    <div className={classes.metronomeControls}>
      <div className={classes.controlGroup}>
        <span>
          {t('metronome.controls.bpm')}
        </span>
        <Input
          className={classes.bpmInput}
          type="number"
          value={inputBpm}
          onChange={(e) => {
            setInputBpm(Number(e.target.value));
          }}
          onBlur={() => handleBpmChange(inputBpm)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleBpmChange(inputBpm);
            }
          }}
        />
        <div className={classes.bpmSliderContainer}>
          <IconButton aria-label="decrease bpm" onClick={() => handleBpmChange(bpm - 1)}>
            <Remove />
          </IconButton>
          <Slider
            aria-label="bpm"
            value={bpm}
            onChange={(_, newValue) => handleBpmChange(newValue as number)}
            min={MIN_BPM}
            max={MAX_BPM}
          />
          <IconButton aria-label="increase bpm" onClick={() => handleBpmChange(bpm + 1)}>
            <Add />
          </IconButton>
        </div>
      </div>
      <div className={classes.controlGroup}>
        <span>
          {t('metronome.controls.timeSig')}
        </span>
        <ButtonGroup variant="contained">
          <Button onClick={() => onTimeSigChange(3)} disabled={timeSig === 3}>
            3/4
          </Button>
          <Button onClick={() => onTimeSigChange(4)} disabled={timeSig === 4}>
            4/4
          </Button>
        </ButtonGroup>
      </div>
      {onToggle && (
        <IconButton aria-label={isOn ? 'stop' : 'start'} onClick={onToggle}>
          {isOn ? <Pause /> : <PlayArrow />}
        </IconButton>
      )}
    </div>
  );
};

export default MetronomeControls;
